<script>
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import { Tippy } from 'vue-tippy';
import AlertMessage from '@/components/AlertMessage.vue';
import EmptyState from '@/components/EmptyState.vue';

export default {
    name: 'ContentBlockTagScores',
    components: { EmptyState, AlertMessage, InertiaSquareLinkButton, ProgressBar, Tippy },
    props: {
        contentBlock: {},
    },
};
</script>
<template>
    <div class="flex justify-end">
        <AlertMessage class="mt-3" type="info">
            <template v-slot:title>
                {{ $t('messages.content_block_scores_page_explanation') }}
            </template>
        </AlertMessage>
    </div>
    <div>
        <div class="p-1">
            <div class="flex justify-between">
                <div class="font-bold">
                    {{ contentBlock.name }}
                </div>
                <div>
                    <InertiaSquareLinkButton
                        v-if="contentBlock.links.show"
                        :href="contentBlock.links.show"
                        action="show"
                    />
                </div>
            </div>
            <EmptyState picture v-if="contentBlock.sections.length === 0" />
            <div class="divide-y divide-gray-300" v-else>
                <div class="mt-3" v-for="section in contentBlock.sections" :key="section.name">
                    <div>
                        {{ section.name }}
                    </div>
                    <ul class="pl-3 divide-y divide-gray-300">
                        <li class="mt-5" v-for="tag in section.tags" :key="tag.id">
                            <div class="md:grid md:grid-cols-4 md:gap-6">
                                <div class="text-sm flex-0 px-2 flex flex-col justify-center">
                                    <div>
                                        {{ tag.name }}
                                    </div>
                                    <div>
                                        <InertiaSquareLinkButton action="show" :href="tag.links.show" />
                                    </div>
                                </div>
                                <div class="flex-1 col-span-3">
                                    <div class="flex flex-col mt-5">
                                        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                                            <div
                                                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                                            >
                                                <table class="divide-y divide-gray-200 w-full">
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                class="max-w-xs px-6 py-1 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                            >
                                                                &nbsp;
                                                            </th>
                                                            <th
                                                                class="max-w-xs md:w-40 px-6 py-1 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                            >
                                                                &nbsp;
                                                            </th>
                                                            <th
                                                                class="max-w-xs px-2 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                            >
                                                                {{ $t('messages.difficulty') }}
                                                            </th>
                                                            <th
                                                                class="max-w-xs px-2 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                            >
                                                                {{ $t('messages.speed') }}
                                                            </th>
                                                            <th
                                                                class="max-w-xs px-2 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                            >
                                                                {{ $t('messages.date') }}
                                                            </th>
                                                            <th
                                                                class="max-w-xs px-2 py-1 bg-gray-50 text-center text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                            >
                                                                {{ $t('messages.precision') }}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr
                                                            v-for="(mode, index) in tag.modes"
                                                            :key="mode.name"
                                                            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                                        >
                                                            <td
                                                                class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                            >
                                                                {{ mode.name }}
                                                            </td>
                                                            <td
                                                                class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                            >
                                                                <Tippy>
                                                                    <ProgressBar
                                                                        :percent="mode.percent"
                                                                        :color="mode.color"
                                                                        height="sm"
                                                                        :show-text="false"
                                                                        class="mt-1 w-40"
                                                                    />
                                                                    <template #content>
                                                                        <div class="font-bold">
                                                                            {{ mode.name }}
                                                                        </div>
                                                                        <div>
                                                                            {{ $tc('messages.questions', 2) }}:
                                                                            {{ mode.total }}
                                                                        </div>
                                                                        <div v-if="mode.total">
                                                                            <span
                                                                                >{{
                                                                                    $t('messages.correct_questions')
                                                                                }}:&nbsp;</span
                                                                            >
                                                                            <span
                                                                                >{{ mode.correct }} / {{ mode.total }} (
                                                                                {{ mode.percent }}%)</span
                                                                            >
                                                                        </div>
                                                                    </template>
                                                                </Tippy>
                                                            </td>
                                                            <td
                                                                class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                            >
                                                                {{ mode.best }}
                                                            </td>
                                                            <td
                                                                class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                            >
                                                                {{ mode.duration }}
                                                            </td>
                                                            <td
                                                                class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                            >
                                                                {{ mode.date }}
                                                            </td>
                                                            <td
                                                                class="max-w-sm truncate px-6 py-1 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                                            >
                                                                {{ mode.score }}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
